import { useState } from 'react';
import { BarChart, Card, DonutChart, Title } from '@tremor/react';
import { formatMxn } from '../lib/format';
import { t } from '../lib/i18n';

export interface Slice {
  /** What goes on the X axis (bar) or as the slice label (donut). */
  label: string;
  value: number;
}

interface Props {
  title: string;
  data: Slice[];
  /** Default visualization. */
  initial?: 'bar' | 'pie';
  /** Tremor color names; cycles through if shorter than data. */
  colors?: string[];
  /** Click a bar/slice to drill in. Receives the slice label. */
  onSliceClick?: (label: string) => void;
}

/**
 * Renders one metric two ways and lets the user flip between them. BarChart
 * for "look at the magnitudes side-by-side"; DonutChart for "see the
 * proportional split". Both share the same slice data — toggling is purely
 * presentation.
 */
export default function ToggleableChart({ title, data, initial = 'bar', colors, onSliceClick }: Props) {
  const [mode, setMode] = useState<'bar' | 'pie'>(initial);

  // Tremor v3 needs the value key to literally match a category string —
  // we use t.series.revenue ("Ingreso") so the legend reads naturally.
  const valueKey = t.series.revenue;
  const barData = data.map((s) => ({ name: s.label, [valueKey]: s.value }));
  const donutData = data.map((s) => ({ name: s.label, value: s.value }));
  const palette = colors ?? ['blue', 'cyan', 'amber', 'emerald', 'violet', 'rose', 'lime', 'orange', 'indigo'];

  return (
    <Card>
      <div className="flex items-start gap-3">
        <Title className="text-base">{title}</Title>
        <div className="ml-auto inline-flex rounded border border-tremor-border text-xs">
          <button
            type="button"
            onClick={() => setMode('bar')}
            className={
              'px-2 py-1 ' +
              (mode === 'bar' ? 'bg-tremor-brand text-tremor-brand-inverted' : 'text-tremor-content hover:text-tremor-content-strong')
            }
          >
            {t.chartToggle.bar}
          </button>
          <button
            type="button"
            onClick={() => setMode('pie')}
            className={
              'px-2 py-1 ' +
              (mode === 'pie' ? 'bg-tremor-brand text-tremor-brand-inverted' : 'text-tremor-content hover:text-tremor-content-strong')
            }
          >
            {t.chartToggle.pie}
          </button>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-tremor-content text-sm">
          {t.chartToggle.empty}
        </div>
      ) : mode === 'bar' ? (
        <BarChart
          className={'mt-3 h-56' + (onSliceClick ? ' cursor-pointer' : '')}
          data={barData}
          index="name"
          categories={[valueKey]}
          colors={[palette[0]]}
          valueFormatter={formatMxn}
          yAxisWidth={72}
          onValueChange={onSliceClick ? (v) => {
            const payload = v as unknown as { name?: string } | null;
            if (payload?.name) onSliceClick(payload.name);
          } : undefined}
        />
      ) : (
        <DonutChart
          className={'mt-3 h-56' + (onSliceClick ? ' cursor-pointer' : '')}
          data={donutData}
          category="value"
          index="name"
          colors={palette}
          valueFormatter={formatMxn}
          onValueChange={onSliceClick ? (v) => {
            const payload = v as unknown as { name?: string } | null;
            if (payload?.name) onSliceClick(payload.name);
          } : undefined}
        />
      )}
    </Card>
  );
}
